import { useState, useEffect, useContext } from "react";
import OAuthClientContext from "../../store/oauthclient-context";
import AuthContext from "../../store/auth-context";
import OAuthAuthorize from "./OAuthAuthorize";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { API_NEW_URL, APP_VERSION } from "../../constants/constants";
import storage from "../../hooks/storage";
import classes from "./OAuth.module.css";
import { useNavigate } from "react-router-dom";

/**
 * OAuthGate Component
 *
 * This component:
 * 1. Checks if OAuth tokens exist (from context/localStorage)
 * 2. If localStorage doesn't have status but user is logged in, verify from backend
 * 3. If YES -> renders children (normal app)
 * 4. If NO -> shows OAuthAuthorize component (authorize button or message)
 */
const OAuthGate = ({ children }) => {
  const navigate = useNavigate();
  const oauthClientCtx = useContext(OAuthClientContext);
  const authCtx = useContext(AuthContext);
  const [isVerifying, setIsVerifying] = useState(false);

  // Extract values from context
  const { setOAuthComplete, hasTokens } = oauthClientCtx;
  const { isLoggedIn, token, clearData } = authCtx;

  // Get subdomain and isAdmin from context, fallback to localStorage on reload
  const cachedUserInfo = storage.get('oauth_user_info');
  const subdomain = oauthClientCtx.subdomain || cachedUserInfo?.subdomain || "";
  const isAdmin = oauthClientCtx.isAdmin || cachedUserInfo?.isAdmin || false;

  useEffect(() => {
    // If localStorage doesn't have oauth_status but user is logged in,
    // verify from backend (covers case where admin authorized while non-admin was logged in)
    const verifyOAuthStatus = async () => {
      const cachedStatus = storage.get('oauth_status');

      // Only verify if: no cached status AND user is logged in AND subdomain exists
      if (!cachedStatus && isLoggedIn && subdomain) {
        setIsVerifying(true);
        try {
          const response = await fetch(
            `${API_NEW_URL}zendesk/oauth/status?subdomain=${subdomain}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
              },
            }
          );

            const data = await response.json();
            if (data?.hasTokens) {
              // Admin has authorized, update context and localStorage
              setOAuthComplete();
            } else {
              console.error("Error checking zendesk status inside verifyOAuthStatus:", data);
            }
        } catch (error) {
          console.error("Error verifying OAuth status:", error);
        }
        setIsVerifying(false);
      }
      if(isLoggedIn && !cachedStatus && !subdomain){ // old user
          clearData(); // Clear auth context state and localStorage
          storage.set("version", APP_VERSION);
          navigate("/auth");
      }
    };

    verifyOAuthStatus();
  }, [isLoggedIn, token, subdomain]);

  // Show loading while verifying
  if (isVerifying) {
    return (
      <div className={classes.loadingContainer}>
        <span className={classes.spinner}>
          <FontAwesomeIcon icon={faCircleNotch} spin />
        </span>
        <p className={classes.loadingText}>Checking authorization...</p>
      </div>
    );
  }

  // If OAuth tokens don't exist, show authorization UI
  if (!hasTokens) {
    return (
      <OAuthAuthorize
        isAdmin={isAdmin}
        subdomain={subdomain}
        onAuthComplete={() => {
          setOAuthComplete();
        }}
      />
    );
  }

  // OAuth tokens exist, render normal app
  return children;
};

export default OAuthGate;
